import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Filter } from "lucide-react";
import { api, peso, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const MANUAL_PRICING_CATS = new Set(["Meats", "Sunjin Feeds"]);
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const FILTERS = [
  { value: "placeholder",       label: "⚠ Placeholder price (≤ ₱1) only" },
  { value: "all",               label: "All products" },
  { value: "Groceries",         label: "Category: Groceries" },
  { value: "Condiments",        label: "Category: Condiments" },
  { value: "Frozen Products",   label: "Category: Frozen Products" },
  { value: "Meats",             label: "Category: Meats (direct selling price)" },
  { value: "Sunjin Feeds",      label: "Category: Sunjin Feeds (sack price)" },
];


function PriceRow({ product, onSaved }) {
  const manual = MANUAL_PRICING_CATS.has(product.category);
  const [cost, setCost]       = useState(product.cost_price ?? "");
  const [margin, setMargin]   = useState(product.margin_percent ?? 15);
  const [price, setPrice]     = useState(product.price ?? 0);
  const [status, setStatus]   = useState("idle");  // idle | saving | saved | error

  // Keep computed price in sync whenever cost or margin changes (only for auto-margin products)
  useEffect(() => {
    if (manual) return;
    const c = Number(cost);
    if (!c) return;
    setPrice(round2(c * (1 + Number(margin || 0) / 100)));
  }, [cost, margin, manual]);

  const save = async () => {
    setStatus("saving");
    try {
      const payload = {
        ...product,
        price: Number(price),
        stock: Number(product.stock),
        cost_price: manual ? (product.cost_price ?? null) : (cost === "" ? null : Number(cost)),
        margin_percent: manual ? (product.margin_percent ?? null) : (margin === "" ? null : Number(margin)),
        subcategory: product.subcategory || null,
        plu: product.plu || null,
        barcode: product.barcode || null,
      };
      // Strip any internal Mongo/Pydantic fields that the PUT handler rejects
      delete payload._id;
      delete payload.created_at;
      await api.put(`/products/${product.id}`, payload);
      setStatus("saved");
      onSaved({ ...product, price: payload.price, cost_price: payload.cost_price, margin_percent: payload.margin_percent });
      setTimeout(() => setStatus("idle"), 1500);
    } catch (e) {
      setStatus("error");
      toast.error(`${product.name}: ${formatApiError(e)}`);
      setTimeout(() => setStatus("idle"), 2500);
    }
  };

  const rowClass = status === "saved" ? "bg-[#EAF5EC]" : status === "error" ? "bg-[#FFE8E8]" : "";

  return (
    <tr className={`transition-colors ${rowClass}`} data-testid={`bulk-row-${product.id}`}>
      <td className="p-2">
        <div className="flex items-center gap-2 min-w-[220px]">
          {product.image_url ? (
            <img src={product.image_url} alt="" className="h-8 w-8 rounded object-cover bg-[#F1F3EE] flex-shrink-0" />
          ) : (
            <div className="h-8 w-8 rounded bg-[#F1F3EE] flex-shrink-0" />
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[#1C2826] line-clamp-1">{product.name}</div>
            <div className="text-[10px] text-[#738380]">{product.category} · {product.unit}</div>
          </div>
        </div>
      </td>
      {manual ? (
        <>
          <td className="p-2 text-xs text-[#A0AAA7]" colSpan={2}>— manual pricing —</td>
          <td className="p-2">
            <Input
              type="number"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="h-8 w-24 text-sm"
              data-testid={`bulk-price-${product.id}`}
            />
          </td>
        </>
      ) : (
        <>
          <td className="p-2">
            <Input
              type="number"
              step="0.01"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder="—"
              className="h-8 w-20 text-sm"
              data-testid={`bulk-cost-${product.id}`}
            />
          </td>
          <td className="p-2">
            <Input
              type="number"
              step="0.5"
              value={margin}
              onChange={(e) => setMargin(e.target.value)}
              className="h-8 w-16 text-sm"
              data-testid={`bulk-margin-${product.id}`}
            />
          </td>
          <td className="p-2 text-sm font-bold text-[#2F5233]" data-testid={`bulk-sell-${product.id}`}>
            {peso(price)}
          </td>
        </>
      )}
      <td className="p-2 text-right">
        <Button
          size="sm"
          onClick={save}
          disabled={status === "saving"}
          className="h-8 bg-[#2F5233] hover:bg-[#1F3D24] text-white rounded-md"
          data-testid={`bulk-save-${product.id}`}
        >
          {status === "saving" && <Loader2 className="h-3 w-3 animate-spin" />}
          {status === "saved" && <Check className="h-3 w-3" />}
          {(status === "idle" || status === "error") && "Save"}
        </Button>
      </td>
    </tr>
  );
}


export default function BulkPriceEditor({ open, onClose, products, onReload }) {
  const [filter, setFilter] = useState("placeholder");
  const [search, setSearch] = useState("");

  const visible = useMemo(() => {
    let list = products;
    if (filter === "placeholder") {
      list = list.filter((p) => (p.price ?? 0) <= 1);
    } else if (filter !== "all") {
      list = list.filter((p) => p.category === filter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q));
    }
    return list.filter((p) => p.active !== false).sort((a, b) => a.name.localeCompare(b.name));
  }, [products, filter, search]);

  const handleSaved = (updated) => {
    // Mutate local copy so the row reflects the new price immediately
    const idx = products.findIndex((p) => p.id === updated.id);
    if (idx >= 0) products[idx] = updated;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Bulk Price Editor</DialogTitle>
          <p className="text-xs text-[#738380] mt-0.5">
            Enter Purchase Price → selling price auto-computes with your margin %. For Meats &amp; Sunjin Feeds, enter the selling price directly. Click Save per row.
          </p>
        </DialogHeader>

        <div className="flex items-center gap-2 py-2 border-y border-[#E1E5DF]">
          <Filter className="h-4 w-4 text-[#738380]" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-64 h-9 bg-white text-sm" data-testid="bulk-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FILTERS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            placeholder="Search name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 max-w-xs text-sm"
            data-testid="bulk-search"
          />
          <div className="ml-auto text-xs text-[#738380]" data-testid="bulk-count">
            {visible.length} product{visible.length === 1 ? "" : "s"}
          </div>
        </div>

        <div className="overflow-y-auto flex-1">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F9F7] text-left text-[11px] uppercase tracking-wider text-[#4A5D5A] sticky top-0 z-10">
              <tr>
                <th className="p-2">Product</th>
                <th className="p-2">Purchase ₱</th>
                <th className="p-2">Margin %</th>
                <th className="p-2">Selling ₱</th>
                <th className="p-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E1E5DF]">
              {visible.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-sm text-[#738380]">Nothing matches this filter.</td></tr>
              )}
              {visible.map((p) => (
                <PriceRow key={p.id} product={p} onSaved={handleSaved} />
              ))}
            </tbody>
          </table>
        </div>

        <div className="pt-3 border-t border-[#E1E5DF] flex justify-end gap-2">
          <Button variant="outline" onClick={() => { onReload(); onClose(); }} data-testid="bulk-close">Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
